import React, { useState } from 'react';
import axios from 'axios';

interface Product {
  product_name: string;
  variant_name: string;
  variant_type: string;
  id: number;
  product_id: number;
  product_description: string;
  price: string;
  compare_at_price: string;
}

interface PreviewData {
  customer_name: string;
  phone_no: string;
  original_customer_phone: string;
  date_of_checkout: string;
  products: Product[];
  engati_payload: {
    'user.channel': string;
    'user.phone_no': string;
    customer_name: string;
    abandoned_cart_products: string;
    date_of_checkout: string;
  };
}

const PocAbcPreview: React.FC = () => {
  const [phoneNo, setPhoneNo] = useState('919876543210');
  const [loading, setLoading] = useState(false);
  const [triggeringCall, setTriggeringCall] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callResult, setCallResult] = useState<any | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);

  const handleLoadPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://crm-api.zeroharm.in/api';
      const response = await axios.post(`${apiUrl}/poc/abc-preview`, {
        phone_no: phoneNo
      });

      if (response.data.success) {
        setPreviewData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load preview');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error loading preview');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleTriggerCall = async () => {
    if (!previewData) {
      setError('Please load ABC orders first');
      return;
    }

    setTriggeringCall(true);
    setError(null);
    setCallResult(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://crm-api.zeroharm.in/api';
      const response = await axios.post(`${apiUrl}/poc/trigger-engati-call`, {
        phone_no: previewData.phone_no,
        customer_name: previewData.customer_name,
        date_of_checkout: previewData.date_of_checkout,
        products: previewData.products
      });

      if (response.data.success) {
        setCallResult({
          success: true,
          message: response.data.message,
          data: response.data.data
        });
      } else {
        setError(response.data.message || 'Failed to trigger call');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error triggering call');
    } finally {
      setTriggeringCall(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui' }}>
      <h1>ABC Lead Engati Preview (POC)</h1>
      <p style={{ color: '#666' }}>
        Load the latest abandoned cart lead and see what data would be sent to Engati voice agent.
        <br />
        <strong>⚠️ PREVIEW ONLY - No calls will be triggered</strong>
      </p>

      {/* Input Section */}
      <div style={{
        border: '1px solid #ddd',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        backgroundColor: '#f9f9f9'
      }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Test Phone Number
        </label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={phoneNo}
            onChange={(e) => setPhoneNo(e.target.value)}
            placeholder="919876543210"
            style={{
              flex: 1,
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px'
            }}
            disabled={loading}
          />
          <button
            onClick={handleLoadPreview}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: loading ? '#ccc' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Loading...' : 'Load ABC Orders'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #f00',
          color: '#c00',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Preview Data */}
      {previewData && (
        <>
          {/* Customer Info Card */}
          <div style={{
            border: '1px solid #ddd',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            backgroundColor: '#f0f5ff'
          }}>
            <h2 style={{ marginTop: 0 }}>👤 Customer Info</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <strong>Customer Name:</strong>
                <div>{previewData.customer_name}</div>
              </div>
              <div>
                <strong>Test Phone (for call):</strong>
                <div>{previewData.phone_no}</div>
              </div>
              <div>
                <strong>Original Customer Phone:</strong>
                <div>{previewData.original_customer_phone || '—'}</div>
              </div>
              <div>
                <strong>Cart Abandoned Date:</strong>
                <div>{previewData.date_of_checkout}</div>
              </div>
            </div>
          </div>

          {/* Products Card */}
          <div style={{
            border: '1px solid #ddd',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h2 style={{ marginTop: 0 }}>📦 Products in Cart ({previewData.products.length})</h2>
            {previewData.products.length === 0 ? (
              <p style={{ color: '#666' }}>No products found</p>
            ) : (
              previewData.products.map((product, idx) => (
                <div
                  key={idx}
                  style={{
                    border: '1px solid #e0e0e0',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    {product.product_name}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                    <div>
                      <strong>Variant:</strong> {product.variant_name}
                    </div>
                    <div>
                      <strong>Price:</strong> ₹{product.price}
                    </div>
                    <div>
                      <strong>Product ID:</strong> {product.product_id}
                    </div>
                    <div>
                      <strong>Variant ID:</strong> {product.id}
                    </div>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    <strong>Description:</strong> {product.product_description.substring(0, 150)}...
                  </div>
                </div>
              ))
            )}
          </div>

          {/* JSON Payload */}
          <div style={{
            border: '1px solid #ddd',
            padding: '15px',
            borderRadius: '8px',
            backgroundColor: '#f5f5f5'
          }}>
            <h2 style={{ marginTop: 0 }}>📋 JSON Payload (for Engati API)</h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #ddd',
              padding: '12px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px',
              overflowX: 'auto',
              maxHeight: '400px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {JSON.stringify(previewData.engati_payload, null, 2)}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={() => copyToClipboard(JSON.stringify(previewData.engati_payload, null, 2))}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                📋 Copy JSON
              </button>
              <button
                onClick={handleTriggerCall}
                disabled={triggeringCall}
                style={{
                  padding: '8px 16px',
                  backgroundColor: triggeringCall ? '#ccc' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: triggeringCall ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {triggeringCall ? '📞 Triggering...' : '📞 Trigger Call to Engati'}
              </button>
            </div>
          </div>

          {/* Call Result */}
          {callResult && (
            <div style={{
              marginTop: '20px',
              border: callResult.success ? '1px solid #28a745' : '1px solid #dc3545',
              padding: '15px',
              borderRadius: '8px',
              backgroundColor: callResult.success ? '#d4edda' : '#f8d7da'
            }}>
              <h2 style={{ marginTop: 0, color: callResult.success ? '#155724' : '#721c24' }}>
                {callResult.success ? '✅ Call Triggered Successfully' : '❌ Call Failed'}
              </h2>
              <div style={{ color: callResult.success ? '#155724' : '#721c24' }}>
                <strong>Message:</strong> {callResult.message}
              </div>
              {callResult.data && (
                <div style={{ marginTop: '12px' }}>
                  <strong>Response Data:</strong>
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    padding: '10px',
                    borderRadius: '4px',
                    marginTop: '8px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {JSON.stringify(callResult.data, null, 2)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer Note */}
          <div style={{
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            color: '#856404'
          }}>
            <strong>ℹ️ How This Works:</strong>
            <ul style={{ margin: '8px 0' }}>
              <li>📌 <strong>Load ABC Orders:</strong> Fetches latest abandoned cart lead from database</li>
              <li>📞 <strong>Trigger Call:</strong> Sends your test phone number + cart data to Engati voice agent API</li>
              <li>✅ <strong>No Real Customers:</strong> Uses test phone number you enter, not the original customer phone</li>
              <li>🛍️ <strong>Real Product Data:</strong> Products come from actual Shopify orders</li>
              <li>📋 <strong>JSON Copy:</strong> Export payload for manual testing or debugging</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default PocAbcPreview;
